import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpenIcon, 
  UserIcon, 
  ArrowLeftIcon,
  TrophyIcon,
  ClockIcon,
  BoltIcon,
  ArrowRightOnRectangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const Profile = ({ onLogout }) => {
  const studentId = localStorage.getItem('studentId');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Mock user data
  const [userProfile, setUserProfile] = useState({
    studentId: studentId,
    name: "Kemi Adebayo",
    email: "kemi.adebayo@email.com",
    joinDate: "September 2024",
    avatar: null,
    bio: "Passionate about technology and continuous learning. Currently focusing on full-stack development and cloud technologies.",
    location: "Lagos, Nigeria",
    timezone: "WAT (UTC+1)",
    studyGoal: "2 hours per day",
    preferredLearningStyle: "Visual + Hands-on"
  });

  const [editForm, setEditForm] = useState({ ...userProfile });

  const mockStats = {
    totalCourses: 4,
    completedCourses: 1,
    inProgressCourses: 3,
    totalHours: 156,
    studyStreak: 12,
    badges: 8,
    averageScore: 87,
    rank: "Advanced Learner",
    level: 15,
    xp: 2450,
    xpToNextLevel: 550
  };

  const mockBadges = [
    { id: 1, name: "First Course", description: "Completed your first course", icon: "🎓", earned: true, date: "Oct 2024" },
    { id: 2, name: "Speed Learner", description: "Completed 5 lessons in one day", icon: "⚡", earned: true, date: "Oct 2024" },
    { id: 3, name: "Consistent Learner", description: "7-day study streak", icon: "🔥", earned: true, date: "Nov 2024" },
    { id: 4, name: "Code Master", description: "Completed 10 coding challenges", icon: "💻", earned: true, date: "Nov 2024" },
    { id: 5, name: "Team Player", description: "Helped 5 fellow students", icon: "🤝", earned: false, date: null },
    { id: 6, name: "Perfect Score", description: "Scored 100% on a final exam", icon: "🏆", earned: false, date: null }
  ];

  const mockRecentActivity = [
    { id: 1, type: "course_complete", title: "Completed React Hooks lesson", course: "Full Stack Web Development", date: "2 hours ago" },
    { id: 2, type: "badge_earned", title: "Earned 'Consistent Learner' badge", course: null, date: "1 day ago" },
    { id: 3, type: "quiz_passed", title: "Passed JavaScript Fundamentals Quiz", course: "Full Stack Web Development", date: "2 days ago" },
    { id: 4, type: "course_start", title: "Started Python for Data Science", course: "Python for Data Science", date: "3 days ago" }
  ];

  const handleSaveProfile = () => {
    setUserProfile({ ...editForm });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditForm({ ...userProfile });
    setIsEditing(false);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'course_complete': return '✅';
      case 'badge_earned': return '🏆';
      case 'quiz_passed': return '📝';
      case 'course_start': return '🚀';
      default: return '📚';
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="card text-center">
      <div className={`w-12 h-12 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm font-medium text-gray-700 mb-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );

  const BadgeCard = ({ badge }) => (
    <div className={`card ${!badge.earned ? 'opacity-50' : ''} relative overflow-hidden`}>
      <div className="text-center">
        <div className="text-4xl mb-3">{badge.icon}</div>
        <h4 className="font-bold text-gray-900 mb-1">{badge.name}</h4>
        <p className="text-sm text-gray-600 mb-2">{badge.description}</p>
        {badge.earned ? (
          <span className="text-xs text-green-600 font-medium">Earned {badge.date}</span>
        ) : (
          <span className="text-xs text-gray-400">Not earned yet</span>
        )}
      </div>
      {badge.earned && (
        <div className="absolute top-2 right-2">
          <CheckIcon className="w-5 h-5 text-green-500" />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link 
                to="/dashboard" 
                className="mr-4 p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-white/50"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Link>
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl mr-3">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Profile</h1>
                <p className="text-sm text-gray-500">Manage your account settings</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">Student ID: {studentId}</p>
                  <p className="text-xs text-gray-500">Level {mockStats.level}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-white" />
                </div>
              </div>

              <button 
                onClick={onLogout}
                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Profile Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="card text-center">
              <div className="w-24 h-24 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{userProfile.name}</h2>
              <p className="text-sm text-gray-600 mb-2">{userProfile.email}</p>
              <p className="text-xs text-gray-500 mb-4">Member since {userProfile.joinDate}</p>
              
              {/* Level Progress */}
              <div className="bg-gray-100 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Level {mockStats.level}</span>
                  <span className="text-sm text-primary-600">{mockStats.xp} XP</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full"
                    style={{ width: `${(mockStats.xp / (mockStats.xp + mockStats.xpToNextLevel)) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{mockStats.xpToNextLevel} XP to next level</p>
              </div>

              <button 
                onClick={() => setIsEditing(true)}
                className="btn-primary text-sm w-full"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            </div>

            {/* Quick Stats */}
            <div className="card">
              <h3 className="font-bold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Study Streak</span>
                  <span className="font-semibold text-orange-600">{mockStats.studyStreak} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Average Score</span>
                  <span className="font-semibold text-green-600">{mockStats.averageScore}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Hours</span>
                  <span className="font-semibold text-blue-600">{mockStats.totalHours}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Badges Earned</span>
                  <span className="font-semibold text-purple-600">{mockStats.badges}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-xl">
              {['overview', 'achievements', 'activity', 'settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium capitalize transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Learning Stats */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Learning Progress</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard 
                      icon={BookOpenIcon}
                      title="Total Courses"
                      value={mockStats.totalCourses}
                      color="from-blue-500 to-blue-600"
                    />
                    <StatCard 
                      icon={TrophyIcon}
                      title="Completed"
                      value={mockStats.completedCourses}
                      color="from-green-500 to-green-600"
                    />
                    <StatCard 
                      icon={ClockIcon}
                      title="Study Hours"
                      value={mockStats.totalHours}
                      color="from-purple-500 to-purple-600"
                    />
                    <StatCard 
                      icon={BoltIcon}
                      title="Current Streak"
                      value={`${mockStats.studyStreak} days`}
                      color="from-orange-500 to-orange-600"
                    />
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Recent Activity</h3>
                  <div className="card">
                    <div className="space-y-4">
                      {mockRecentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-b-0">
                          <div className="text-2xl">{getActivityIcon(activity.type)}</div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{activity.title}</p>
                            {activity.course && (
                              <p className="text-sm text-gray-600">{activity.course}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">{activity.date}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'achievements' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Badges & Achievements</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {mockBadges.map((badge) => (
                      <BadgeCard key={badge.id} badge={badge} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Learning Analytics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="card">
                      <h4 className="font-bold text-gray-900 mb-4">Weekly Study Time</h4>
                      <div className="h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-500">📊 Chart visualization would go here</p>
                      </div>
                    </div>
                    <div className="card">
                      <h4 className="font-bold text-gray-900 mb-4">Course Progress</h4>
                      <div className="h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-500">📈 Progress chart would go here</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h3>
                  <div className="card">
                    <form className="space-y-6">
                      <div className="space-y-6">
                        <div>
                          <label className="form-label mb-2">
                            Full Name
                          </label>
                          <input
                            type="text"
                            value={isEditing ? editForm.name : userProfile.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            disabled={!isEditing}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="form-label mb-2">
                            Email
                          </label>
                          <input
                            type="email"
                            value={isEditing ? editForm.email : userProfile.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            disabled={!isEditing}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Location
                          </label>
                          <input
                            type="text"
                            value={isEditing ? editForm.location : userProfile.location}
                            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                            disabled={!isEditing}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Daily Study Goal
                          </label>
                          <input
                            type="text"
                            value={isEditing ? editForm.studyGoal : userProfile.studyGoal}
                            onChange={(e) => setEditForm({ ...editForm, studyGoal: e.target.value })}
                            disabled={!isEditing}
                            className="input-field"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bio
                        </label>
                        <textarea
                          rows="4"
                          value={isEditing ? editForm.bio : userProfile.bio}
                          onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                          disabled={!isEditing}
                          className="input-field resize-none"
                        />
                      </div>

                      {isEditing && (
                        <div className="flex items-center space-x-4">
                          <button
                            type="button"
                            onClick={handleSaveProfile}
                            className="btn-primary flex items-center"
                          >
                            <CheckIcon className="w-4 h-4 mr-2" />
                            Save Changes
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="btn-secondary flex items-center"
                          >
                            <XMarkIcon className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile; 